import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiClient } from "../lib/api";
import { useAuth } from "../lib/auth.jsx";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Lock, AlertCircle } from "lucide-react";
import { toast } from "sonner";

export default function ChangePassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const isForced = params.get("force") === "1";
  const { user, setAuth, auth } = useAuth();

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (next !== confirm) {
      setError("A nova senha e a confirmação não coincidem.");
      return;
    }
    setLoading(true);
    try {
      await apiClient.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Senha atualizada com sucesso.");
      if (auth) {
        const updated = { ...auth, must_change_password: false, user: { ...auth.user, must_change_password: false } };
        setAuth(updated);
        localStorage.setItem("soiin_auth", JSON.stringify(updated));
      }
      navigate("/", { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || "Falha ao alterar a senha.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="px-6 md:px-12 py-12 max-w-2xl mx-auto" data-testid="change-password-page">
      <Card>
        <CardHeader>
          <CardTitle className="font-display text-2xl">
            {isForced ? "Defina sua nova senha" : "Alterar senha"}
          </CardTitle>
          <CardDescription>
            Mínimo 8 caracteres e ao menos 1 caractere especial. Conta: {user?.email}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="change-password-form">
            <div className="space-y-2">
              <Label htmlFor="current">Senha atual</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="current"
                  type="password"
                  required
                  value={current}
                  onChange={(e) => setCurrent(e.target.value)}
                  className="pl-9"
                  data-testid="current-password-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="new">Nova senha</Label>
              <Input
                id="new"
                type="password"
                required
                value={next}
                onChange={(e) => setNext(e.target.value)}
                data-testid="new-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm">Confirme a nova senha</Label>
              <Input
                id="confirm"
                type="password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                data-testid="confirm-password-input"
              />
            </div>

            {error && (
              <div className="flex items-start gap-2 text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2">
                <AlertCircle className="w-4 h-4 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <div className="flex items-center gap-3">
              <Button type="submit" disabled={loading} data-testid="change-password-submit">
                {loading ? "Salvando…" : "Salvar"}
              </Button>
              {!isForced && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => navigate("/")}
                  data-testid="change-password-cancel"
                >
                  Cancelar
                </Button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
